/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU16Decoder,
  getU16Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { CPMM_PROGRAM_ADDRESS } from '../programs';

export const UPDATE_CONFIG_DISCRIMINATOR = new Uint8Array([
  29, 158, 252, 191, 10, 83, 219, 99,
]);

export function getUpdateConfigDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    UPDATE_CONFIG_DISCRIMINATOR,
  );
}

export type UpdateConfigInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountConfig extends string | AccountMeta<string> = string,
  TAccountAdmin extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountConfig extends string
        ? WritableAccount<TAccountConfig>
        : TAccountConfig,
      TAccountAdmin extends string
        ? ReadonlySignerAccount<TAccountAdmin> &
            AccountSignerMeta<TAccountAdmin>
        : TAccountAdmin,
      ...TRemainingAccounts,
    ]
  >;

export type UpdateConfigInstructionData = {
  discriminator: ReadonlyUint8Array;
  maxSwapFeeBps: number;
  maxFeeSplitBps: number;
  protocolFeeEnabled: boolean;
  protocolFeeBps: number;
  hookAllowlist: Array<Address>;
};

export type UpdateConfigInstructionDataArgs = {
  maxSwapFeeBps: number;
  maxFeeSplitBps: number;
  protocolFeeEnabled: boolean;
  protocolFeeBps: number;
  hookAllowlist: Array<Address>;
};

export function getUpdateConfigInstructionDataEncoder(): Encoder<UpdateConfigInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['maxSwapFeeBps', getU16Encoder()],
      ['maxFeeSplitBps', getU16Encoder()],
      ['protocolFeeEnabled', getBooleanEncoder()],
      ['protocolFeeBps', getU16Encoder()],
      ['hookAllowlist', getArrayEncoder(getAddressEncoder())],
    ]),
    (value) => ({ ...value, discriminator: UPDATE_CONFIG_DISCRIMINATOR }),
  );
}

export function getUpdateConfigInstructionDataDecoder(): Decoder<UpdateConfigInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['maxSwapFeeBps', getU16Decoder()],
    ['maxFeeSplitBps', getU16Decoder()],
    ['protocolFeeEnabled', getBooleanDecoder()],
    ['protocolFeeBps', getU16Decoder()],
    ['hookAllowlist', getArrayDecoder(getAddressDecoder())],
  ]);
}

export function getUpdateConfigInstructionDataCodec(): Codec<
  UpdateConfigInstructionDataArgs,
  UpdateConfigInstructionData
> {
  return combineCodec(
    getUpdateConfigInstructionDataEncoder(),
    getUpdateConfigInstructionDataDecoder(),
  );
}

export type UpdateConfigInput<
  TAccountConfig extends string = string,
  TAccountAdmin extends string = string,
> = {
  config: Address<TAccountConfig>;
  admin: TransactionSigner<TAccountAdmin>;
  maxSwapFeeBps: UpdateConfigInstructionDataArgs['maxSwapFeeBps'];
  maxFeeSplitBps: UpdateConfigInstructionDataArgs['maxFeeSplitBps'];
  protocolFeeEnabled: UpdateConfigInstructionDataArgs['protocolFeeEnabled'];
  protocolFeeBps: UpdateConfigInstructionDataArgs['protocolFeeBps'];
  hookAllowlist: UpdateConfigInstructionDataArgs['hookAllowlist'];
};

export function getUpdateConfigInstruction<
  TAccountConfig extends string,
  TAccountAdmin extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: UpdateConfigInput<TAccountConfig, TAccountAdmin>,
  config?: { programAddress?: TProgramAddress },
): UpdateConfigInstruction<TProgramAddress, TAccountConfig, TAccountAdmin> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: true },
    admin: { value: input.admin ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('config', accounts.config),
      getAccountMeta('admin', accounts.admin),
    ],
    data: getUpdateConfigInstructionDataEncoder().encode(
      args as UpdateConfigInstructionDataArgs,
    ),
    programAddress,
  } as UpdateConfigInstruction<TProgramAddress, TAccountConfig, TAccountAdmin>);
}

export type ParsedUpdateConfigInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    config: TAccountMetas[0];
    admin: TAccountMetas[1];
  };
  data: UpdateConfigInstructionData;
};

export function parseUpdateConfigInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedUpdateConfigInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 2,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { config: getNextAccount(), admin: getNextAccount() },
    data: getUpdateConfigInstructionDataDecoder().decode(instruction.data),
  };
}
